import _ from 'lodash';

// Supermove
import {gql} from '@supermove/graphql';
import {Form} from '@supermove/hooks';
import {CallrailIntegrationModel} from '@supermove/models';
import {withFragment, Validation} from '@supermove/utils';
import {ValidationErrors} from '@supermove/utils/src/Validation';

// App
import CallrailIntegrationMode from '@shared/modules/CallrailIntegration/enums/CallrailIntegrationMode';
import SetupCallrailIntegrationStep from '@shared/modules/CallrailIntegration/enums/SetupCallrailIntegrationStep';

export interface SetupCallrailIntegrationFormType {
  // Basic fields
  organizationId?: string;
  callrailApiKey: string;
  callrailAccountId: string;
  callrailAccountName: string;
  callrailCompanyId: string;
  callrailCompanyName: string;
  mode: string;

  // Optional fields (required based on mode)
  projectTypeId?: string;
  jobTypeId?: string;

  // Excluded phone numbers
  callrailExcludedPhoneNumbers?: string[];
}

const _new = ({organizationId}: {organizationId: string}): SetupCallrailIntegrationFormType => {
  return {
    organizationId,
    callrailApiKey: '',
    callrailAccountId: '',
    callrailAccountName: '',
    callrailCompanyId: '',
    callrailCompanyName: '',
    mode: CallrailIntegrationMode.ENHANCE_CALLS,
    projectTypeId: undefined,
    jobTypeId: undefined,
    callrailExcludedPhoneNumbers: [],
  };
};

const edit = withFragment<CallrailIntegrationModel, SetupCallrailIntegrationFormType>(
  (callrailIntegration) => ({
    organizationId: callrailIntegration.organizationId,
    callrailApiKey: callrailIntegration.callrailApiKey || '',
    callrailAccountId: callrailIntegration.callrailAccountId,
    callrailAccountName: callrailIntegration.callrailAccountName,
    callrailCompanyId: callrailIntegration.callrailCompanyId,
    callrailCompanyName: callrailIntegration.callrailCompanyName,
    mode: callrailIntegration.mode,
    projectTypeId: callrailIntegration.projectTypeId
      ? callrailIntegration.projectTypeId
      : undefined,
    jobTypeId: callrailIntegration.jobTypeId ? callrailIntegration.jobTypeId : undefined,
    callrailExcludedPhoneNumbers: callrailIntegration.callrailExcludedPhoneNumbers
      ? callrailIntegration.callrailExcludedPhoneNumbers.map((x) => x.number)
      : [],
  }),
  gql`
    fragment SetupCallrailIntegrationForm_edit on CallrailIntegration {
      id
      organizationId
      callrailApiKey
      callrailAccountId
      callrailAccountName
      callrailCompanyId
      callrailCompanyName
      mode
      projectTypeId
      jobTypeId
      callrailExcludedPhoneNumbers {
        id
        number
      }
    }
  `,
);

export interface SetupCallrailIntegrationFormToFormType {
  organizationId?: string;
  callrailApiKey: string;
  callrailAccountId: string;
  callrailAccountName: string;
  callrailCompanyId: string;
  callrailCompanyName: string;
  mode: string;
  projectTypeId?: string;
  jobTypeId?: string;
  callrailExcludedPhoneNumbers?: string[];
}

export type FormikSetupCallrailIntegrationFormType = Form<{
  setupCallrailIntegrationForm: SetupCallrailIntegrationFormToFormType;
}>;

const toForm = (
  setupCallrailIntegrationForm: SetupCallrailIntegrationFormType,
): SetupCallrailIntegrationFormToFormType => ({
  organizationId: setupCallrailIntegrationForm.organizationId,
  callrailApiKey: setupCallrailIntegrationForm.callrailApiKey,
  callrailAccountId: setupCallrailIntegrationForm.callrailAccountId,
  callrailAccountName: setupCallrailIntegrationForm.callrailAccountName,
  callrailCompanyId: setupCallrailIntegrationForm.callrailCompanyId,
  callrailCompanyName: setupCallrailIntegrationForm.callrailCompanyName,
  mode: setupCallrailIntegrationForm.mode,
  projectTypeId: setupCallrailIntegrationForm.projectTypeId,
  jobTypeId: setupCallrailIntegrationForm.jobTypeId,
  callrailExcludedPhoneNumbers: setupCallrailIntegrationForm.callrailExcludedPhoneNumbers,
});

const toMutation = (setupCallrailIntegrationForm: SetupCallrailIntegrationFormToFormType) => ({
  organizationId: setupCallrailIntegrationForm.organizationId,
  callrailApiKey: setupCallrailIntegrationForm.callrailApiKey,
  callrailAccountId: setupCallrailIntegrationForm.callrailAccountId,
  callrailAccountName: setupCallrailIntegrationForm.callrailAccountName,
  callrailCompanyId: setupCallrailIntegrationForm.callrailCompanyId,
  callrailCompanyName: setupCallrailIntegrationForm.callrailCompanyName,
  mode: setupCallrailIntegrationForm.mode,
  projectTypeId: setupCallrailIntegrationForm.projectTypeId,
  jobTypeId: setupCallrailIntegrationForm.jobTypeId,
  callrailExcludedPhoneNumbers: setupCallrailIntegrationForm.callrailExcludedPhoneNumbers,
});

// Step-based validation functions
const validateApiKey = ({
  form,
}: {
  form: FormikSetupCallrailIntegrationFormType;
}): ValidationErrors => {
  return Validation.requiredStringField({
    form,
    field: 'setupCallrailIntegrationForm.callrailApiKey',
    message: 'Please enter a CallRail API key.',
  });
};

const validateAccountAndCompany = ({
  form,
}: {
  form: FormikSetupCallrailIntegrationFormType;
}): ValidationErrors => {
  const errors: ValidationErrors = {};

  const accountIdError = Validation.requiredStringField({
    form,
    field: 'setupCallrailIntegrationForm.callrailAccountId',
    message: 'Please select a CallRail account.',
  });

  const companyIdError = Validation.requiredStringField({
    form,
    field: 'setupCallrailIntegrationForm.callrailCompanyId',
    message: 'Please select a CallRail company.',
  });

  return {
    ...accountIdError,
    ...companyIdError,
  };
};

const validateProjectSettings = ({
  form,
}: {
  form: FormikSetupCallrailIntegrationFormType;
}): ValidationErrors => {
  const errors: ValidationErrors = {};
  const mode = _.get(form.values, 'setupCallrailIntegrationForm.mode');

  if (mode === CallrailIntegrationMode.CREATE_CALLS) {
    const projectTypeIdError = Validation.requiredStringField({
      form,
      field: 'setupCallrailIntegrationForm.projectTypeId',
      message: 'Please select a project type.',
    });

    const jobTypeIdError = Validation.requiredStringField({
      form,
      field: 'setupCallrailIntegrationForm.jobTypeId',
      message: 'Please select a job type.',
    });

    Object.assign(errors, projectTypeIdError, jobTypeIdError);
  }

  return errors;
};

// Validate excluded phone numbers - each phone number should be 10 digits
const validateExcludedPhoneNumbers = ({
  form,
}: {
  form: FormikSetupCallrailIntegrationFormType;
}): ValidationErrors => {
  const errors: ValidationErrors = {};
  const excludedPhoneNumbers: string[] = _.get(
    form.values,
    'setupCallrailIntegrationForm.callrailExcludedPhoneNumbers',
    [],
  );
  const invalidNumbers: string[] = [];
  const seenNumbers = new Set<string>();
  let hasDuplicates = false;

  // Check each phone number
  excludedPhoneNumbers.forEach((number: string) => {
    if (number) {
      // Check for valid 10-digit number
      if (!/^\d{10}$/.test(number)) {
        invalidNumbers.push(number);
      }

      // Check for duplicates
      if (seenNumbers.has(number)) {
        hasDuplicates = true;
      } else {
        seenNumbers.add(number);
      }
    }
  });

  if (invalidNumbers.length > 0) {
    errors['setupCallrailIntegrationForm.callrailExcludedPhoneNumbers'] =
      `Invalid phone number(s): ${invalidNumbers.join(', ')}. Numbers must be 10 digits with no spaces or special characters.`;
  } else if (hasDuplicates) {
    errors['setupCallrailIntegrationForm.callrailExcludedPhoneNumbers'] =
      'Duplicate phone numbers are not allowed.';
  }

  return errors;
};

const validateReview = ({
  form,
}: {
  form: FormikSetupCallrailIntegrationFormType;
}): ValidationErrors => {
  // For review step, we validate all previous steps
  const errors: ValidationErrors = {};
  const mode = _.get(form.values, 'setupCallrailIntegrationForm.mode');

  // Always validate API Key and Account/Company
  Object.assign(errors, validateApiKey({form}), validateAccountAndCompany({form}));

  // Only validate Project Settings and Excluded Phone Numbers for CREATE_CALLS mode
  if (mode === CallrailIntegrationMode.CREATE_CALLS) {
    Object.assign(errors, validateProjectSettings({form}), validateExcludedPhoneNumbers({form}));
  }

  return errors;
};

const validateFormForStep = ({
  stepKind,
  form,
}: {
  stepKind: string;
  form: FormikSetupCallrailIntegrationFormType;
}): ValidationErrors => {
  // Skip validation for PROJECT_SETTINGS step if mode is ENHANCE_CALLS
  if (stepKind === SetupCallrailIntegrationStep.PROJECT_SETTINGS) {
    const mode = _.get(form.values, 'setupCallrailIntegrationForm.mode');
    if (mode === CallrailIntegrationMode.ENHANCE_CALLS) {
      return {}; // No validation needed for this step with ENHANCE_CALLS mode
    }
  }

  // Skip validation for EXCLUDED_PHONE_NUMBERS step if mode is ENHANCE_CALLS
  if (stepKind === SetupCallrailIntegrationStep.EXCLUDED_PHONE_NUMBERS) {
    const mode = _.get(form.values, 'setupCallrailIntegrationForm.mode');
    if (mode === CallrailIntegrationMode.ENHANCE_CALLS) {
      return {}; // No validation needed for this step with ENHANCE_CALLS mode
    }
  }

  switch (stepKind) {
    case SetupCallrailIntegrationStep.API_KEY:
      return validateApiKey({form});
    case SetupCallrailIntegrationStep.ACCOUNT_AND_COMPANY:
      return validateAccountAndCompany({form});
    case SetupCallrailIntegrationStep.PROJECT_SETTINGS:
      return validateProjectSettings({form});
    case SetupCallrailIntegrationStep.EXCLUDED_PHONE_NUMBERS:
      return validateExcludedPhoneNumbers({form}); // Validate phone numbers
    case SetupCallrailIntegrationStep.REVIEW:
      return validateReview({form});
    default:
      return {};
  }
};

const validateStep = async ({
  stepKind,
  form,
}: {
  stepKind: string;
  form: FormikSetupCallrailIntegrationFormType;
}): Promise<ValidationErrors> => {
  const errors = validateFormForStep({stepKind, form});
  await form.setTouched(_.mapValues(errors, () => true));
  await form.setErrors(errors);
  return errors;
};

// Complete validation for the entire form
const validate = ({form}: {form: FormikSetupCallrailIntegrationFormType}): ValidationErrors => {
  return validateReview({form});
};

const SetupCallrailIntegrationForm = {
  toForm,
  toMutation,
  new: _new,
  edit,
  validate,
  validateStep,
};

export default SetupCallrailIntegrationForm;
